import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon, Logo } from '../components/Icon.js';
import { BRAND } from '../branding.js';

type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled';
type UiState = 'checking' | 'succeeded' | 'pending-timeout' | 'direct';

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_MS = 20_000;

/** Кнопки билета: POST /api/ticket/:paymentId/links (apps/api/src/routes/public/ticket.ts). */
interface TicketLinks {
  ticketNumber: string;
  eventName: string;
  ticketUrl: string | null;
  appleUrl: string | null;
  googleUrl: string | null;
  calendarUrl: string;
  siteUrl: string;
}

type Device = 'iphone' | 'android' | 'other';

/** iPad в Safari выдаёт себя за Mac — у него сенсорный экран; Wallet на iPad нет. */
function detectDevice(): Device {
  const ua = navigator.userAgent;
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

// Номер билета выдаётся сразу после смены статуса — может отстать на секунду.
const LINKS_TRIES = 4;
const LINKS_RETRY_MS = 1500;

/**
 * Ссылки кнопок билета для оплаченного платежа. null — кнопок нет: у продукта
 * не включены билеты или не заполнено событие (404), или кнопками этой
 * страницы уже воспользовались (410) — тогда билет в письме и у бота.
 */
async function fetchTicketLinks(paymentId: string): Promise<TicketLinks | null> {
  for (let i = 0; i < LINKS_TRIES; i++) {
    const res = await fetch(`/api/ticket/${encodeURIComponent(paymentId)}/links`, { method: 'POST' });
    if (res.ok) return (await res.json()) as TicketLinks;
    if (res.status !== 404) return null;
    await new Promise((r) => setTimeout(r, LINKS_RETRY_MS));
  }
  return null;
}

export function PaymentSuccess(): JSX.Element {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const paymentId = params.get('payment');
  const [state, setState] = useState<UiState>(paymentId ? 'checking' : 'direct');
  const [postPurchase, setPostPurchase] = useState<{ url: string; label: string } | null>(null);
  const [ticket, setTicket] = useState<TicketLinks | null>(null);

  useEffect(() => {
    if (!paymentId) return;
    let stopped = false;
    const started = Date.now();

    const tick = async (): Promise<void> => {
      try {
        const res = await fetch(`/api/payment-status/${encodeURIComponent(paymentId)}`);
        if (!res.ok) throw new Error(`http_${res.status}`);
        const data = (await res.json()) as {
          status: PaymentStatus;
          postPurchaseUrl?: string;
          postPurchaseLabel?: string;
        };
        if (stopped) return;
        if (data.status === 'succeeded') {
          if (data.postPurchaseUrl) {
            setPostPurchase({
              url: data.postPurchaseUrl,
              label: data.postPurchaseLabel || 'Открыть доступ',
            });
          }
          setState('succeeded');
          // Кнопки билета — только у продуктов с билетами и событием; ошибка не мешает странице.
          fetchTicketLinks(paymentId)
            .then((t) => {
              if (!stopped) setTicket(t);
            })
            .catch(() => undefined);
          return;
        }
        if (data.status === 'failed' || data.status === 'canceled') {
          nav('/fail', { replace: true });
          return;
        }
        if (Date.now() - started >= POLL_MAX_MS) {
          setState('pending-timeout');
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        if (!stopped) setState('pending-timeout');
      }
    };
    void tick();

    return () => {
      stopped = true;
    };
  }, [paymentId, nav]);

  if (state === 'checking') {
    return (
      <Result
        icon={<Icon name="refresh" size={32} stroke={2} />}
        accent="var(--fg-muted)"
        title="Проверяем платёж…"
        body="Это займёт пару секунд."
      />
    );
  }

  if (state === 'pending-timeout') {
    return (
      <Result
        icon={<Icon name="refresh" size={32} stroke={2} />}
        accent="var(--fg-muted)"
        title="Платёж в обработке"
        body="Это может занять несколько минут. Мы пришлём подтверждение на email, как только получим ответ от банка."
      />
    );
  }

  return (
    <Result
      icon={<Icon name="check" size={36} stroke={2} />}
      accent="var(--accent)"
      title="Оплата прошла"
      body={
        ticket
          ? 'Спасибо! Ваш билет ниже, он же придёт на email.'
          : postPurchase
            ? 'Спасибо! Ссылка ниже — ваш доступ. Она же придёт на email.'
            : 'Спасибо! Квитанция и доступ будут отправлены на email в ближайшие минуты.'
      }
      action={postPurchase ?? undefined}
      extra={ticket ? <TicketBlock links={ticket} /> : undefined}
    />
  );
}

/**
 * Блок билета после оплаты: «Скачать билет», кошельки под устройство и
 * календарь. Кнопки на одно нажатие: после любой блок исчезает — дальше билет
 * в письме (и у бота, если он есть у продукта).
 */
function TicketBlock({ links }: { links: TicketLinks }): JSX.Element {
  const [used, setUsed] = useState(false);
  const device = detectDevice();
  const wallets = (
    [
      ['apple', links.appleUrl, 'Apple Wallet'],
      ['google', links.googleUrl, 'Google Wallet'],
    ] as const
  ).filter(([w, url]) => url && (device === 'other' || (device === 'iphone') === (w === 'apple')));
  // Ссылка открывается в новой вкладке, блок гаснет сразу после нажатия.
  const spend = (): void => {
    setTimeout(() => setUsed(true), 0);
  };
  const box: CSSProperties = {
    marginTop: 22,
    paddingTop: 18,
    borderTop: '1px solid var(--hairline)',
    display: 'grid',
    gap: 8,
    textAlign: 'left',
  };
  if (used) {
    return (
      <div style={{ ...box, fontSize: 13, color: 'var(--fg-subtle)', textAlign: 'center' }}>
        Билет открыт в новой вкладке. Снова его можно открыть кнопками в письме.
      </div>
    );
  }
  return (
    <div style={box}>
      <div>
        <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Билет № {links.ticketNumber}</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          <bdi>{links.eventName}</bdi>
        </div>
      </div>
      {links.ticketUrl && (
        <a
          href={links.ticketUrl}
          target="_blank"
          rel="noopener"
          onClick={spend}
          className="btn btn-primary"
          style={{ justifyContent: 'center', width: '100%' }}
        >
          Скачать билет
        </a>
      )}
      {wallets.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${wallets.length}, 1fr)`, gap: 8 }}>
          {wallets.map(([w, url, label]) => (
            <a
              key={w}
              href={url!}
              target="_blank"
              rel="noopener"
              onClick={spend}
              className="btn"
              style={{
                justifyContent: 'center',
                width: '100%',
                background: '#000',
                color: '#fff',
                borderColor: '#000',
                borderRadius: 999,
                gap: 8,
              }}
            >
              <img src={`/ticket-assets/${w}-wallet-icon.svg`} alt="" style={{ height: 20 }} />
              <span style={{ whiteSpace: 'nowrap' }}>
                <small style={{ fontWeight: 400 }}>Add to</small> {label}
              </span>
            </a>
          ))}
        </div>
      )}
      <a
        href={links.calendarUrl}
        target="_blank"
        rel="noopener"
        onClick={spend}
        className="btn"
        style={{ justifyContent: 'center', width: '100%' }}
      >
        Добавить в календарь
      </a>
      {links.siteUrl && (
        <a
          href={links.siteUrl}
          target="_blank"
          rel="noopener"
          style={{ fontSize: 13, color: 'var(--fg-subtle)', textAlign: 'center', marginTop: 2 }}
        >
          Сайт события
        </a>
      )}
    </div>
  );
}

export function PaymentFail(): JSX.Element {
  return (
    <Result
      icon={<Icon name="x" size={36} stroke={2} />}
      accent="var(--danger-fg)"
      title="Оплата не прошла"
      body="Платёж был отменён или отклонён. Попробуйте ещё раз или другой способ оплаты."
      retry
    />
  );
}

function Result({
  icon,
  accent,
  title,
  body,
  retry,
  action,
  extra,
}: {
  icon: JSX.Element;
  accent: string;
  title: string;
  body: string;
  retry?: boolean;
  action?: { url: string; label: string };
  extra?: JSX.Element;
}): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg)',
        padding: 20,
      }}
    >
      {/* width 100% + maxWidth: в сетке с placeItems фиксированная ширина не сжималась,
          и на телефоне страница раскладывалась шире экрана. */}
      <div style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 24,
            justifyContent: 'center',
          }}
        >
          <Logo size={24} />
          <div style={{ fontWeight: 600, fontSize: 15 }}>{BRAND.name}</div>
        </div>
        <div className="card" style={{ padding: '36px 28px' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 18px',
              background: 'var(--bg-sunken)',
              color: accent,
            }}
          >
            {icon}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>
            {title}
          </h1>
          <p style={{ margin: 0, color: 'var(--fg-subtle)', fontSize: 14, lineHeight: 1.5 }}>
            {body}
          </p>
          {action && (
            <a
              href={action.url}
              className="btn btn-primary"
              style={{ justifyContent: 'center', marginTop: 22, width: '100%' }}
            >
              <bdi>{action.label}</bdi>
            </a>
          )}
          {extra}
          {retry && (
            <a
              href={BRAND.siteUrl || '/'}
              className="btn btn-primary"
              style={{ justifyContent: 'center', marginTop: 22, width: '100%' }}
            >
              Вернуться на сайт
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
